import {
  useCreateOrganisationAccountingCategory,
  useDeleteOrganisationAccountingCategory,
  useOrganisationAccountingCategories,
  useUpdateOrganisationAccountingCategory,
} from '@my/directory-api/hooks';
import type { BaseStatusValue, OrganisationAccountingCategory } from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface AccountingCategoriesSectionProps {
  /** Owning organisation id; the panel stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Accounting categories — the analysis code that travels beside the nominal
 * on every charge and invoice line (a QuickBooks Class, a Xero Tracking
 * Category, a Sage Department). Shown on the Organisation page between Tax
 * Schemas and Nominal Codes while "Use Accounting Categories" is on.
 *
 * <p>
 * Same table + in-place editor as Nominal Codes. Name and code are required;
 * the code is short (`CHEM`) and is what lines carry. Whichever of the two the
 * accounting package keys categories on is what gets posted — there is no
 * separate "accounting value". A category that lines already carry is set
 * Inactive rather than deleted.
 * </p>
 */
export function AccountingCategoriesSection({ organisationId }: AccountingCategoriesSectionProps) {
  const { t } = useTranslation('organisation');
  const listQuery = useOrganisationAccountingCategories(organisationId);
  const createMutation = useCreateOrganisationAccountingCategory();
  const updateMutation = useUpdateOrganisationAccountingCategory();
  const deleteMutation = useDeleteOrganisationAccountingCategory();

  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<CategoryForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OrganisationAccountingCategory | null>(null);
  const editorRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (editingId === null) return;
    const id = requestAnimationFrame(() => {
      editorRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const startEdit = (row: OrganisationAccountingCategory) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;
    const description = draft.description.trim() || null;

    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync({
          organisationId,
          code: draft.code.trim(),
          name: draft.name.trim(),
          description,
          // New ones go to the end; there are no reorder controls yet.
          displayOrder: listQuery.data?.length ?? 0,
          status: draft.status,
        });
        showToast({ type: 'success', message: t('accountingCategories.added') });
      } else {
        await updateMutation.mutateAsync({
          organisationId,
          body: {
            categoryId: editingId,
            code: draft.code.trim(),
            name: draft.name.trim(),
            description,
            displayOrder: draft.displayOrder,
            status: draft.status,
          },
        });
        showToast({ type: 'success', message: t('accountingCategories.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('accountingCategories.saveFailed'),
      });
    }
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('accountingCategories.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('accountingCategories.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('accountingCategories.addAria')}
        onClick={startNew}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('accountingCategories.add')}
      </button>
    ) : null;

  const COLS = 4;

  return (
    <FormSection title={t('accountingCategories.title')} columns={1} actions={headerAction}>
      {listQuery.isPending && <Loading label={t('accountingCategories.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('accountingCategories.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && listQuery.data.length === 0 && editingId !== 'new' && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
          <p className="text-sm text-slate-600">{t('accountingCategories.empty')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('accountingCategories.emptyHint')}</p>
        </div>
      )}

      {listQuery.isSuccess && (listQuery.data.length > 0 || editingId === 'new') && (
        <div className="overflow-hidden rounded-md">
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full min-w-[640px] table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('accountingCategories.table.name')}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('accountingCategories.table.code')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('accountingCategories.table.description')}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t('accountingCategories.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500">
                {editingId === 'new' && (
                  <tr ref={editorRowRef}>
                    <td colSpan={COLS} className="bg-slate-50 p-3">
                      <div className="max-w-2xl">
                        <CategoryEditor
                          draft={draft}
                          onChange={setDraft}
                          submitted={submitted}
                          onSave={handleSave}
                          onCancel={cancel}
                          isBusy={isBusy}
                        />
                      </div>
                    </td>
                  </tr>
                )}

                {listQuery.data.map((row) => {
                  if (editingId === row.id) {
                    return (
                      <tr key={row.id} ref={editorRowRef}>
                        <td colSpan={COLS} className="bg-slate-50 p-3">
                          <div className="max-w-2xl">
                            <CategoryEditor
                              draft={draft}
                              onChange={setDraft}
                              submitted={submitted}
                              onSave={handleSave}
                              onCancel={cancel}
                              onDelete={() => setPendingDelete(row)}
                              isBusy={isBusy}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const otherEditing = editingId !== null;
                  return (
                    <tr
                      key={row.id}
                      className={otherEditing ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}
                      onDoubleClick={otherEditing ? undefined : () => startEdit(row)}
                    >
                      <td className="px-3 py-0.5 text-left">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium text-slate-900">{row.name}</span>
                          {row.status === 'Inactive' && (
                            <span className="inline-flex items-center rounded-sm bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                              {t('accountingCategories.inactiveBadge')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-0.5 text-center font-medium text-slate-700">{row.code}</td>
                      <td className="max-w-[360px] truncate px-3 py-0.5 text-left text-slate-600">
                        {row.description || '—'}
                      </td>
                      <td className="px-3 py-0.5 text-right">
                        <button
                          type="button"
                          aria-label={t('accountingCategories.ariaEdit', { code: row.code })}
                          onClick={() => startEdit(row)}
                          disabled={otherEditing}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('accountingCategories.deleteDialog.title')}
        message={
          pendingDelete
            ? t('accountingCategories.deleteDialog.message', {
                code: pendingDelete.code,
                name: pendingDelete.name,
              })
            : null
        }
        confirmLabel={t('accountingCategories.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Form
// ────────────────────────────────────────────────────────────────────────────

interface CategoryForm {
  name: string;
  code: string;
  description: string;
  /** Not edited here — kept so an update writes back what it read. */
  displayOrder: number;
  status: BaseStatusValue;
}

function emptyForm(): CategoryForm {
  return { name: '', code: '', description: '', displayOrder: 0, status: 'Active' };
}

function toForm(row: OrganisationAccountingCategory): CategoryForm {
  return {
    name: row.name,
    code: row.code,
    description: row.description ?? '',
    displayOrder: row.displayOrder,
    status: row.status,
  };
}

function isFormValid(form: CategoryForm): boolean {
  return (
    form.code.trim().length > 0 &&
    form.code.length <= 32 &&
    form.name.trim().length > 0 &&
    form.name.length <= 128 &&
    form.description.length <= 255
  );
}

interface CategoryEditorProps {
  draft: CategoryForm;
  onChange: (next: CategoryForm) => void;
  submitted: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

function CategoryEditor({
  draft,
  onChange,
  submitted,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: CategoryEditorProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof CategoryForm>(key: K, value: CategoryForm[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label={requiredLabel(t('accountingCategories.fields.name'))}
          name="name"
          autoComplete="off"
          value={draft.name}
          maxLength={128}
          onChange={(e) => set('name', e.target.value)}
          error={submitted && !draft.name.trim()}
          errorMessage={t('accountingCategories.validation.nameRequired')}
        />
        <TextInput
          label={requiredLabel(t('accountingCategories.fields.code'))}
          name="code"
          autoComplete="off"
          value={draft.code}
          maxLength={32}
          // The server stores it upper-case; show it that way while typing.
          onChange={(e) => set('code', e.target.value.toUpperCase())}
          error={submitted && !draft.code.trim()}
          errorMessage={t('accountingCategories.validation.codeRequired')}
          placeholder={t('accountingCategories.fields.codePlaceholder')}
        />
        <TextInput
          label={t('accountingCategories.fields.description')}
          name="description"
          autoComplete="off"
          value={draft.description}
          maxLength={255}
          onChange={(e) => set('description', e.target.value)}
        />
        <Switch
          label={t('accountingCategories.fields.status')}
          name="status"
          checked={draft.status === 'Active'}
          enabledLabel={t('accountingCategories.fields.statusActive')}
          disabledLabel={t('accountingCategories.fields.statusInactive')}
          onChange={(e) => set('status', e.currentTarget.checked ? 'Active' : 'Inactive')}
        />
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('accountingCategories.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('accountingCategories.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('accountingCategories.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Field label with the red asterisk the other required fields use. */
function requiredLabel(text: string) {
  return (
    <>
      {text} <span className="text-red-500">*</span>
    </>
  );
}
